import { inject, Injectable } from '@angular/core';
import { catchError, defer, finalize, map, Observable, of, switchMap } from 'rxjs';
import { DevicesApiService } from '../../../pages/equipment/devices/api/devices-api.service';
import {
  Devices,
  DevicesPayload,
  DevicesSelectionSummary,
  TableDevices
} from '../../../pages/equipment/devices/components/devices.models';
import { SelectionPayload } from '@portal/models/bulk-operations/selection.types';
import { MapValidator } from '../../../shared/components/listing-page/helpers/map-validator';
import { mapLink } from '../../../shared/constants';
import { DeviceForAPI, Meter, MeterTable } from './devices-data.types';
import { TimezoneService } from '../../utility/timezone.service';
import { LoaderService } from '../../utility/loader.service';
import { ExportDataSource } from 'src/app/shared/components/listing-page/services/listing-page-model/listing-page-model.types';

@Injectable({
  providedIn: 'root'
})
export class DevicesDataService {
  private readonly devicesApiService = inject(DevicesApiService);
  private readonly timezoneService = inject(TimezoneService);
  private readonly loaderService = inject(LoaderService);

  prepareDataForTable(tableState: DevicesPayload): Observable<TableDevices> {
    const initState = { data: [], total: 0, totalHiddenCount: 0 };
    // Grid data doesn't need the selection context; only the selection summary does.
    const { selection: _selection, ...gridPayload } = tableState;
    // Keep the loader on for the whole getDevices + getSelectionSummary sequence, so the grid is
    // only re-enabled once every piece of data is ready (not after the first call).
    return defer(() => {
      this.loaderService.show();
      return this.devicesApiService.getDevices(gridPayload);
    }).pipe(
      switchMap((devices: Devices) => {
        // Fetch the selection summary whenever there is an actual selection (manual ids, exclusions
        // or select-all) so totalSelectedCount/totalHiddenCount stay correct across filter changes.
        const selection = tableState.selection;
        const hasSelection =
          !!selection &&
          (selection.isSelectAll ||
            !!selection.uniqueIDs?.length ||
            !!selection.excludedUniqueIDs?.length ||
            !!selection.selectAllContext);
        if (!hasSelection) {
          return of({ devices, selectionSummary: null as DevicesSelectionSummary | null });
        }
        // Query the summary even when the page is empty (e.g. filters return no rows) so the
        // backend's authoritative selected/hidden counts survive instead of resetting to 0.
        const uniqueUIDsOnPage = devices?.items?.map(({ deviceUID }) => deviceUID) ?? [];
        const summaryPayload: DevicesPayload = {
          ...tableState,
          selection: { ...tableState.selection, uniqueUIDsOnPage } as SelectionPayload
        };
        return this.devicesApiService
          .getSelectionSummary(summaryPayload)
          .pipe(map(selectionSummary => ({ devices, selectionSummary })));
      }),
      map(({ devices, selectionSummary }: { devices: Devices; selectionSummary: DevicesSelectionSummary | null }) => {
        if (!devices) return initState;

        const { items, totalCount } = devices;
        const { totalHiddenCount, totalSelectedCount, checkedDevicesOnPage } = selectionSummary ?? {
          totalHiddenCount: 0
        };
        const tableList = items?.map((device: Meter) => ({
          ...device,
          accountNumbers: device.servicePoints?.map(({ fullAccountNumber }) => fullAccountNumber ?? ''),
          serviceLocationIds: device.servicePoints?.map(({ serviceLocationId }) =>
            serviceLocationId ? `${serviceLocationId}` : ''
          ),
          serviceLocationAddress: device.servicePoints?.map(({ serviceLocationAddress }) =>
            serviceLocationAddress ? `${serviceLocationAddress}` : ''
          ),
          fullCustomerNumberUrl: device.servicePoints?.map(
            ({ fullCustomerNumber }) => fullCustomerNumber?.replace('/', '-') ?? ''
          ),
          location: device.geoMetaData && MapValidator.isValidCoords(device.geoMetaData?.latitude) ? mapLink : null,
          lastHeard: device.lastHeard ? this.timezoneService.formatLong(device.lastHeard) : null,
          dcuStatus: device.dcuStatus || device.dcuStatus === 0 ? `${device.dcuStatus}%` : device.dcuStatus
        })) as MeterTable[];

        return { data: tableList, total: totalCount, totalHiddenCount, totalSelectedCount, checkedDevicesOnPage };
      }),
      catchError(err => {
        console.error(err);
        return of(initState);
      }),
      finalize(() => this.loaderService.hide())
    );
  }

  export(payload: Parameters<ExportDataSource['export']>[0]): Observable<DeviceForAPI[]> {
    return this.devicesApiService.exportDevices(payload);
  }
}
